import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { QRCodeSVG } from 'qrcode.react';
import {
  Users,
  Swords,
  Copy,
  Check,
  Play,
  Clock,
  Trophy,
  Flame,
  QrCode,
  ArrowRight,
  LogOut,
  Globe,
  Edit3,
  Send,
  Lock,
  Unlock,
  UserPlus,
  RefreshCw,
  Sparkles,
  Crown,
} from 'lucide-react';
import {
  BIBLE_CATEGORIES,
  DIFFICULTY_LEVELS,
} from '../data/verifiedQuestions.ts';
import { UI_TRANSLATIONS, SupportedLanguage } from '../i18n/translations.ts';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import { formatDisplayNumber } from '../config/scoringConfig.ts';

interface MultiplayerViewProps {
  userUid: string;
  username: string;
  avatarUrl?: string | null;
  country: string;
  language: SupportedLanguage;
  onMatchFinished: () => void;
  onSharePrompt?: (defaultContent: string, badgeCode?: string) => void;
}

interface AvailableRoom {
  roomPin: string;
  name: string;
  hostUid: string;
  hostName: string;
  hostAvatar?: string | null;
  isPrivate: boolean;
  category: string;
  difficulty: string;
  playerCount: number;
}

export const MultiplayerView: React.FC<MultiplayerViewProps> = ({
  userUid,
  username,
  avatarUrl,
  country,
  language,
  onMatchFinished,
  onSharePrompt,
}) => {
  const t = UI_TRANSLATIONS[language] || UI_TRANSLATIONS.en;
  const socketRef = useRef<Socket | null>(null);

  const [roomState, setRoomState] = useState<any | null>(null);
  const [joinPinInput, setJoinPinInput] = useState('');
  const [createRoomName, setCreateRoomName] = useState(
    `${username}'s Scripture Arena`
  );
  const [createCategory, setCreateCategory] = useState('ALL CATEGORIES');
  const [createDifficulty, setCreateDifficulty] = useState('Intermediate');
  const [createCount, setCreateCount] = useState(5);
  const [isPrivateRoom, setIsPrivateRoom] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [copiedPin, setCopiedPin] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [mySubmittedAnswer, setMySubmittedAnswer] = useState<string | null>(
    null
  );

  // Host room name editing in lobby
  const [isEditingRoomName, setIsEditingRoomName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState('');

  // Available public/private rooms list
  const [availableRooms, setAvailableRooms] = useState<AvailableRoom[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Room Invite notification & invite modal
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteUsernameInput, setInviteUsernameInput] = useState('');
  const [inviteStatus, setInviteStatus] = useState<string | null>(null);
  const [incomingInvite, setIncomingInvite] = useState<{
    roomPin: string;
    roomName: string;
    isPrivate: boolean;
    inviterName: string;
  } | null>(null);

  const fetchAvailableRooms = async () => {
    setLoadingRooms(true);
    try {
      const res = await fetch('/api/match/rooms');
      if (res.ok) {
        const data = await res.json();
        setAvailableRooms(data.rooms || []);
      }
    } catch {
      // Non-blocking
    } finally {
      setLoadingRooms(false);
    }
  };

  useEffect(() => {
    fetchAvailableRooms();
    const interval = setInterval(fetchAvailableRooms, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const socket = io(window.location.origin, {
      transports: ['polling', 'websocket'],
    });
    socketRef.current = socket;

    socket.on('mp:room_state', (state) => {
      setRoomState(state);
      setEditingNameValue(state.name || '');
      const me = state.players?.find((p: any) => p.uid === userUid);
      if (state.status === 'playing') {
        if (me && !me.hasAnswered) {
          setMySubmittedAnswer(null);
        }
      }
      if (state.status === 'revealed' && me && me.hasAnswered) {
        const sfxKey = `mp-${state.roomPin}-q${state.currentQuestionIndex}`;
        if ((me.lastPointsEarned || 0) > 0) {
          soundEngine.playCorrect(sfxKey);
        } else {
          soundEngine.playWrong(sfxKey);
        }
      }
      if (state.status === 'finished') {
        if (!me || me.rank === 1) {
          soundEngine.playVictoryFanfare(`mp-win-${state.roomPin}`);
        }
        onMatchFinished();
      }
    });

    socket.on('mp:timer_tick', ({ timeRemaining }) => {
      setRoomState((prev: any) =>
        prev ? { ...prev, timeRemaining } : prev
      );
    });

    socket.on('mp:room_invite', (invite) => {
      const target = String(invite.targetUsername || '').trim().toLowerCase();
      const myUser = username.trim().toLowerCase();
      if (!target || target === myUser) {
        setIncomingInvite({
          roomPin: invite.roomPin,
          roomName: invite.roomName,
          isPrivate: invite.isPrivate,
          inviterName: invite.inviterName,
        });
        soundEngine.playClick();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [userUid, username]);

  const handleCreateRoom = () => {
    if (!socketRef.current) return;
    soundEngine.playClick();
    soundEngine.resetSessionSounds();
    setStatusError(null);
    setIsConnecting(true);

    socketRef.current.emit(
      'mp:create_room',
      {
        uid: userUid,
        username,
        avatarUrl: avatarUrl || null,
        country,
        name: createRoomName.trim() || `${username}'s Scripture Arena`,
        category: createCategory,
        difficulty: createDifficulty,
        questionCount: createCount,
        isPrivate: isPrivateRoom,
        timerSeconds: 20,
        language,
      },
      (res: any) => {
        setIsConnecting(false);
        if (!res?.ok) {
          setStatusError(res?.error || 'Could not create multiplayer room');
        }
      }
    );
  };

  const handleJoinByPin = (pinToJoin?: string, quickMatch = false) => {
    if (!socketRef.current) return;
    const finalPin = pinToJoin ?? joinPinInput.trim();
    if (!quickMatch && !finalPin) {
      setStatusError('Please enter a valid 6-digit Room PIN to join.');
      return;
    }
    soundEngine.playClick();
    soundEngine.resetSessionSounds();
    setStatusError(null);
    setIsConnecting(true);

    socketRef.current.emit(
      'mp:join_room',
      {
        roomPin: quickMatch ? '' : finalPin,
        quickMatch,
        uid: userUid,
        username,
        avatarUrl: avatarUrl || null,
        country,
        category: createCategory,
        difficulty: createDifficulty,
        language,
      },
      (res: any) => {
        setIsConnecting(false);
        if (!res?.ok) {
          setStatusError(res?.error || 'Unable to join room');
        }
      }
    );
  };

  const handleUpdateRoomName = (e: React.FormEvent) => {
    e.preventDefault();
    if (!socketRef.current || !roomState) return;
    const trimmed = editingNameValue.trim();
    if (!trimmed) return;
    soundEngine.playClick();

    socketRef.current.emit(
      'mp:update_room_name',
      {
        roomPin: roomState.roomPin,
        uid: userUid,
        name: trimmed,
      },
      (res: any) => {
        if (res?.ok) {
          setIsEditingRoomName(false);
        } else {
          setStatusError(res?.error || 'Could not update room name');
        }
      }
    );
  };

  const handleSendInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!socketRef.current || !roomState) return;
    const target = inviteUsernameInput.trim();
    if (!target) return;

    socketRef.current.emit('mp:invite_player', {
      roomPin: roomState.roomPin,
      inviterName: username,
      inviterAvatar: avatarUrl || null,
      targetUsername: target,
    });

    setInviteStatus(`Invitation sent to @${target}!`);
    setTimeout(() => {
      setInviteStatus(null);
      setInviteModalOpen(false);
      setInviteUsernameInput('');
    }, 1500);
  };

  const handleStartMatch = () => {
    if (!socketRef.current || !roomState) return;
    soundEngine.playClick();
    soundEngine.resetSessionSounds();
    socketRef.current.emit('mp:start_match', {
      roomPin: roomState.roomPin,
    });
  };

  const handleSelectAnswer = (option: string) => {
    if (
      !socketRef.current ||
      !roomState ||
      roomState.status !== 'playing' ||
      mySubmittedAnswer !== null
    ) {
      return;
    }
    setMySubmittedAnswer(option);
    socketRef.current.emit('mp:submit_answer', {
      roomPin: roomState.roomPin,
      uid: userUid,
      answer: option,
    });
  };

  const handleNextQuestion = () => {
    if (!socketRef.current || !roomState) return;
    soundEngine.playClick();
    setMySubmittedAnswer(null);
    socketRef.current.emit('mp:next_question', {
      roomPin: roomState.roomPin,
    });
  };

  const copyPinToClipboard = () => {
    if (!roomState?.roomPin) return;
    navigator.clipboard?.writeText(roomState.roomPin).catch(() => {});
    setCopiedPin(true);
    setTimeout(() => setCopiedPin(false), 2000);
  };

  // ---------------------------------------------------------------------------
  // ACTIVE ROOM VIEW (Lobby, Playing, Revealed, Finished)
  // ---------------------------------------------------------------------------
  if (roomState) {
    const isHost = roomState.hostUid === userUid;
    const shareUrl = `${window.location.origin}/?mpPin=${roomState.roomPin}`;
    const myPlayer = roomState.players?.find((p: any) => p.uid === userUid);

    return (
      <div
        className="max-w-5xl mx-auto py-8 px-4 sm:px-6"
        data-testid="multiplayer-active-room"
      >
        {/* Room Header Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-5 mb-6 border-b border-[#262626]">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              {roomState.isPrivate ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30">
                  <Lock className="w-3 h-3" /> Private Room (PIN Required)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                  <Unlock className="w-3 h-3" /> Open Room (Public Arena)
                </span>
              )}

              {roomState.isPrivate && (
                <>
                  <span className="text-[#A3A3A3]">·</span>
                  <span className="text-[#737373] text-[#A3A3A3]">PIN:</span>
                  <span className="font-mono font-bold text-[#A3A3A3] text-sm">
                    {roomState.roomPin}
                  </span>
                </>
              )}

              <span className="text-[#A3A3A3]">·</span>
              <span className="text-[#737373] text-[#A3A3A3] font-medium">{roomState.category}</span>
              <span className="text-[#A3A3A3]">·</span>
              <span className="text-[#737373] text-[#A3A3A3] font-medium">{roomState.difficulty}</span>
            </div>

            {/* Room Name with Creator Edit Capability */}
            {isEditingRoomName && isHost ? (
              <form onSubmit={handleUpdateRoomName} className="flex items-center gap-2 pt-1">
                <input
                  type="text"
                  maxLength={50}
                  value={editingNameValue}
                  onChange={(e) => setEditingNameValue(e.target.value)}
                  className="rounded-lg border border-[#525252] bg-[#1C1C1C] px-3 py-1 text-base font-bold text-[#FAFAFA] focus:outline-none"
                  autoFocus
                />
                <button
                  type="submit"
                  className="px-3 py-1 rounded-lg bg-[#525252] text-white text-xs font-semibold"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditingRoomName(false)}
                  className="px-2.5 py-1 rounded-lg border border-[#262626] text-xs font-medium"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl font-bold text-[#FAFAFA]">
                  {roomState.name}
                </h2>
                {isHost && (
                  <button
                    type="button"
                    onClick={() => setIsEditingRoomName(true)}
                    className="p-1 rounded-lg text-[#A3A3A3] transition-colors"
                    title="Edit room name (Room creator only)"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* PIN & QR code are rendered ONLY for private rooms */}
            {roomState.isPrivate && (
              <>
                <button
                  type="button"
                  onClick={copyPinToClipboard}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#262626] text-xs font-medium text-[#FAFAFA] hover:bg-[#1C1C1C] transition-colors"
                >
                  {copiedPin ? (
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  <span>{copiedPin ? 'Copied PIN' : `PIN: ${roomState.roomPin}`}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowQrModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#262626] text-xs font-medium text-[#FAFAFA] hover:bg-[#1C1C1C] transition-colors"
                >
                  <QrCode className="w-3.5 h-3.5" />
                  <span>QR Invite</span>
                </button>
              </>
            )}

            {/* Invite a Scholar Button */}
            <button
              type="button"
              onClick={() => setInviteModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1C1C1C] text-[#FAFAFA] border border-[#525252] text-xs font-medium hover:bg-[#262626] transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Invite</span>
            </button>

            <button
              type="button"
              onClick={() => {
                soundEngine.playClick();
                setRoomState(null);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#262626] text-xs font-medium text-[#D4D4D4] hover:bg-[#1C1C1C] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Leave</span>
            </button>
          </div>
        </div>

        {/* LOBBY PHASE: PLAYER LIST WITH AVATARS & USERNAME */}
        {roomState.status === 'waiting' && (
          <div className="space-y-6">
            <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 mb-6 border-b border-[#262626]">
                <div>
                  <h3 className="text-lg font-bold text-[#FAFAFA] flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#A3A3A3]" />
                    <span>Arena Scholars ({roomState.players?.length || 0})</span>
                  </h3>
                  <p className="text-xs text-[#737373] text-[#A3A3A3] mt-1">
                    {roomState.isPrivate
                      ? 'Private Room: share the 6-digit PIN with others to join.'
                      : 'Open Room: any scholar can join directly from the dashboard.'}
                  </p>
                </div>

                {isHost ? (
                  <button
                    type="button"
                    onClick={handleStartMatch}
                    disabled={roomState.players?.length < 1}
                    className="min-h-[46px] inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-sm font-semibold shadow-sm transition-colors whitespace-nowrap"
                  >
                    <Play className="w-4 h-4" />
                    <span>Start Match Now</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-2 text-xs font-medium text-[#737373] text-[#A3A3A3] bg-[#1C1C1C] bg-[#1C1C1C] px-4 py-2.5 rounded-xl">
                    <Clock className="w-4 h-4 animate-spin text-[#A3A3A3]" />
                    <span>Waiting for room creator to start match...</span>
                  </div>
                )}
              </div>

              {/* Player Cards showing Avatar Photo, Username & Country */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {roomState.players?.map((p: any) => (
                  <div
                    key={p.uid}
                    className="flex items-center gap-3 p-3.5 rounded-xl border border-[#262626] bg-[#1C1C1C] shadow-xs"
                  >
                    {/* Player Profile Photo */}
                    <div className="w-10 h-10 rounded-full bg-[#1C1C1C] text-[#A3A3A3] font-bold text-sm flex items-center justify-center shrink-0 overflow-hidden border border-amber-400/50">
                      {p.avatarUrl ? (
                        <img
                          src={p.avatarUrl}
                          alt={p.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span>{(p.username || 'QB').slice(0, 2).toUpperCase()}</span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm text-[#FAFAFA] truncate">
                          {p.username}
                        </span>
                        {p.uid === roomState.hostUid && (
                          <span title="Room Creator" className="shrink-0">
                            <Crown className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#737373] text-[#A3A3A3] font-mono">
                        {p.country} · {p.uid === userUid ? '(You)' : 'Ready'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* COUNTDOWN PHASE */}
        {roomState.status === 'countdown' && (
          <div className="py-20 text-center">
            <div className="text-7xl font-mono font-bold text-[#A3A3A3] animate-pulse">
              {roomState.countdownValue}
            </div>
            <p className="text-base text-[#D4D4D4] mt-4 font-semibold">
              Get ready! Scripture match starting in...
            </p>
          </div>
        )}

        {/* PLAYING / REVEALED PHASE */}
        {(roomState.status === 'playing' || roomState.status === 'revealed') &&
          roomState.currentQuestion && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 sm:p-7 shadow-sm">
                  {/* Question Header & Timer */}
                  <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#262626]">
                    <span className="text-xs font-semibold text-[#A3A3A3]">
                      Question {roomState.currentQuestionIndex + 1} of{' '}
                      {roomState.totalQuestions}
                    </span>
                    <div className="flex items-center gap-1.5 font-mono font-bold text-sm text-[#FAFAFA]">
                      <Clock className="w-4 h-4 text-[#A3A3A3]" />
                      <span>{roomState.timeRemaining}s</span>
                    </div>
                  </div>

                  <p className="text-base sm:text-lg font-bold text-[#FAFAFA] mb-6 leading-relaxed">
                    {roomState.currentQuestion.questionText}
                  </p>

                  {/* Multiple Choice Options */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {roomState.currentQuestion.options?.map(
                      (opt: string, idx: number) => {
                        const isSelected = mySubmittedAnswer === opt;
                        const isRevealed = roomState.status === 'revealed';
                        const isRight =
                          isRevealed &&
                          opt === roomState.currentQuestion.correctAnswer;
                        const isWrongSelected =
                          isRevealed && isSelected && !isRight;

                        let style =
                          'border-[#262626] bg-[#1C1C1C] text-[#FAFAFA] hover:border-[#525252]';
                        if (isSelected && !isRevealed) {
                          style =
                            'border-[#525252] bg-[#141414] text-[#FAFAFA] font-semibold ring-1 ring-[#525252]';
                        }
                        if (isRevealed) {
                          if (isRight) {
                            style =
                              'border-emerald-500 bg-emerald-500/10 text-emerald-300 font-semibold';
                          } else if (isWrongSelected) {
                            style =
                              'border-red-500 bg-red-500/10 text-red-300';
                          } else {
                            style =
                              'border-[#262626] opacity-50 text-[#737373]';
                          }
                        }

                        return (
                          <button
                            key={idx}
                            type="button"
                            disabled={
                              roomState.status !== 'playing' ||
                              mySubmittedAnswer !== null
                            }
                            onClick={() => handleSelectAnswer(opt)}
                            className={`p-3.5 sm:p-4 rounded-xl border text-left text-sm transition-all min-h-[50px] flex items-center justify-between ${style}`}
                          >
                            <span>{opt}</span>
                            {isRevealed && isRight && (
                              <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                            )}
                          </button>
                        );
                      }
                    )}
                  </div>

                  {mySubmittedAnswer && roomState.status === 'playing' && (
                    <div className="mt-4 text-xs font-mono text-[#A3A3A3]">
                      Answer locked on server ({mySubmittedAnswer}). Waiting for reveal...
                    </div>
                  )}

                  {roomState.status === 'revealed' && (
                    <div className="mt-6 pt-5 border-t border-[#262626] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="text-xs font-mono text-[#A3A3A3] font-semibold">
                          {roomState.currentQuestion.scriptureReference} ·
                          Correct Answer:{' '}
                          {roomState.currentQuestion.correctAnswer}
                        </div>
                        <p className="text-xs text-[#D4D4D4] mt-1">
                          {roomState.currentQuestion.explanation}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleNextQuestion}
                        className="w-full sm:w-auto min-h-[44px] inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold shrink-0 transition-transform active:scale-[0.98]"
                      >
                        <span>
                          {roomState.currentQuestionIndex + 1 <
                          roomState.totalQuestions
                            ? 'Next Question'
                            : 'Finish Match'}
                        </span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Live Match Standings with Player Avatars */}
              <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm">
                <h4 className="text-sm font-bold text-[#FAFAFA] mb-4 flex items-center gap-1.5">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  <span>Live Match Standings</span>
                </h4>
                <div className="space-y-3">
                  {roomState.players?.map((p: any) => (
                    <div
                      key={p.uid}
                      className="flex items-center justify-between p-3 rounded-xl border border-[#262626]"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {/* Player Avatar */}
                        <div className="w-7 h-7 rounded-full bg-[#1C1C1C] text-[#A3A3A3] text-[11px] font-bold flex items-center justify-center shrink-0 overflow-hidden">
                          {p.avatarUrl ? (
                            <img
                              src={p.avatarUrl}
                              alt={p.username}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span>{(p.username || 'QB').slice(0, 2).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-[#FAFAFA] truncate">
                            #{p.rank} {p.username}
                          </div>
                          <div className="text-[10px] text-[#A3A3A3]">
                            {p.hasAnswered ? 'Locked In' : 'Thinking...'}
                            {p.streak > 1 && (
                              <span className="text-amber-500 ml-1.5">🔥{p.streak}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className={`font-mono tabular-nums font-bold text-sm ${(p.score || 0) <= 0 ? 'text-[#737373]' : 'text-[#A3A3A3]'}`}>
                        {formatDisplayNumber(p.score || 0).text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        {/* FINISHED PODIUM */}
        {roomState.status === 'finished' && (
          <div className="rounded-2xl bg-[#141414] border border-[#262626] p-8 text-center shadow-sm">
            <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h3 className="text-2xl font-bold text-[#FAFAFA] mb-2">
              Multiplayer Match Complete
            </h3>
            <p className="text-sm text-[#737373] mb-6">
              Authoritative server scores have been recorded.
            </p>

            <div className="max-w-md mx-auto space-y-3 mb-8">
              {roomState.players?.map((p: any) => (
                <div
                  key={p.uid}
                  className="flex items-center justify-between p-4 rounded-xl border border-[#262626]"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-lg text-amber-500">
                      #{p.rank}
                    </span>
                    <div className="w-9 h-9 rounded-full bg-[#1C1C1C] text-[#A3A3A3] font-bold text-xs flex items-center justify-center shrink-0 overflow-hidden">
                      {p.avatarUrl ? (
                        <img
                          src={p.avatarUrl}
                          alt={p.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span>{(p.username || 'QB').slice(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-bold text-[#FAFAFA]">
                        {p.username} ({p.country})
                      </div>
                      <div className="text-xs text-[#737373]">
                        {p.correctCount} / {roomState.totalQuestions} correct
                      </div>
                    </div>
                  </div>
                  <div className={`font-mono font-bold text-lg tabular-nums ${(p.score || 0) <= 0 ? 'text-[#737373]' : 'text-[#A3A3A3]'}`}>
                    {formatDisplayNumber(p.score || 0).text}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {onSharePrompt && (
                <button
                  type="button"
                  onClick={() => {
                    const myRank = myPlayer?.rank || 1;
                    const defaultMsg = `Finished #${myRank} in ${roomState.name} with ${formatDisplayNumber(myPlayer?.score || 0).text} points! ⚔️📖`;
                    onSharePrompt(defaultMsg);
                  }}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 text-white text-sm font-semibold flex items-center gap-1.5 shadow-sm"
                >
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span>Share Result to Feed</span>
                </button>
              )}
              {isHost && (
                <button
                  type="button"
                  onClick={handleStartMatch}
                  className="px-5 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-sm font-semibold"
                >
                  Play Rematch
                </button>
              )}
              <button
                type="button"
                onClick={() => setRoomState(null)}
                className="px-5 py-2.5 rounded-xl border border-[#262626] text-sm font-medium text-[#FAFAFA]"
              >
                Back to Lobby
              </button>
            </div>
          </div>
        )}

        {/* QR Invite Modal (Private Rooms Only) */}
        {showQrModal && roomState.isPrivate && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            role="dialog"
            aria-modal="true"
          >
            <div className="w-full max-w-sm rounded-2xl bg-[#141414] border border-[#262626] p-6 text-center">
              <h4 className="text-lg font-bold text-[#FAFAFA] mb-2">
                Scan to Join Room {roomState.roomPin}
              </h4>
              <div className="inline-block p-4 bg-[#1C1C1C] rounded-xl border border-[#262626] my-3">
                <QRCodeSVG value={shareUrl} size={180} fgColor="#FAFAFA" bgColor="transparent" />
              </div>
              <p className="text-xs text-[#737373] font-mono break-all mb-4">
                {shareUrl}
              </p>
              <button
                type="button"
                onClick={() => setShowQrModal(false)}
                className="w-full py-2.5 rounded-xl bg-[#525252] text-white text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Invite a Scholar Modal */}
        {inviteModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            role="dialog"
            aria-modal="true"
          >
            <div className="w-full max-w-sm rounded-2xl bg-[#141414] border border-[#262626] p-6">
              <h4 className="text-base font-bold text-[#FAFAFA] mb-1">
                Invite Scholar to Arena
              </h4>
              <p className="text-xs text-[#737373] text-[#A3A3A3] mb-4">
                Send an immediate real-time match invitation to any user.
              </p>

              <form onSubmit={handleSendInvite} className="space-y-3">
                <div>
                  <label
                    htmlFor="invite-username-input"
                    className="block text-xs font-semibold text-[#D4D4D4] mb-1"
                  >
                    Target Username
                  </label>
                  <input
                    id="invite-username-input"
                    type="text"
                    required
                    placeholder="e.g. Priscilla"
                    value={inviteUsernameInput}
                    onChange={(e) => setInviteUsernameInput(e.target.value)}
                    className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2 text-xs text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
                  />
                </div>

                {inviteStatus && (
                  <div className="text-xs font-medium text-emerald-400">
                    {inviteStatus}
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setInviteModalOpen(false)}
                    className="px-3.5 py-2 rounded-xl border border-[#262626] text-xs font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold flex items-center gap-1.5"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>Send Invite</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // DASHBOARD VIEW (When not in a room)
  // ---------------------------------------------------------------------------
  return (
    <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#FAFAFA] mb-2">
          {t.multiplayerLobby}
        </h1>
        <p className="text-sm text-[#737373] text-[#A3A3A3]">
          Compete in real-time synchronized matches with shared question sets,
          open community arenas, and private fellowship rooms.
        </p>
      </div>

      {/* Floating Incoming Invite Notification Banner */}
      {incomingInvite && (
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-[#262626] to-[#1C1C1C] border border-[#525252] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#525252] text-white flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-bold text-[#FAFAFA]">
                Match Invitation from {incomingInvite.inviterName}
              </div>
              <div className="text-xs text-[#D4D4D4]">
                Join <strong>{incomingInvite.roomName}</strong> (
                {incomingInvite.isPrivate ? 'Private Room' : 'Open Room'})
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                handleJoinByPin(incomingInvite.roomPin);
                setIncomingInvite(null);
              }}
              className="px-4 py-2 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold transition-colors"
            >
              Accept &amp; Join
            </button>
            <button
              type="button"
              onClick={() => setIncomingInvite(null)}
              className="px-3 py-2 rounded-xl border border-[#262626] text-xs font-medium text-[#D4D4D4] hover:bg-[#1C1C1C]"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {statusError && (
        <div
          role="alert"
          className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-400"
        >
          {statusError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Quick Global Match & Join by PIN */}
        <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm flex flex-col justify-between space-y-6">
          <div>
            <div className="w-10 h-10 rounded-xl bg-[#1C1C1C] text-[#A3A3A3] flex items-center justify-center mb-4">
              <Globe className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-bold text-[#FAFAFA] mb-1">
              {t.quickMatch}
            </h2>
            <p className="text-xs text-[#737373] text-[#A3A3A3] mb-5">
              Jump straight into an open global Scripture room or start an instant matchmaking queue.
            </p>
            <button
              type="button"
              disabled={isConnecting}
              onClick={() => handleJoinByPin(undefined, true)}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-lg shadow-orange-950/30"
            >
              <Swords className="w-4 h-4" />
              <span>Find Global Match Now</span>
            </button>
          </div>

          <div className="pt-6 border-t border-[#262626]">
            <label
              htmlFor="mp-pin-input"
              className="block text-xs font-semibold text-[#D4D4D4] mb-2"
            >
              {t.joinByPin}
            </label>
            <div className="flex gap-2">
              <input
                id="mp-pin-input"
                type="text"
                maxLength={6}
                placeholder="Enter 6-digit PIN"
                value={joinPinInput}
                onChange={(e) => setJoinPinInput(e.target.value)}
                className="flex-1 rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm font-mono text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              />
              <button
                type="button"
                disabled={isConnecting}
                onClick={() => handleJoinByPin()}
                className="px-4 py-2.5 rounded-xl bg-[#1C1C1C] text-white text-[#FAFAFA] text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                Join
              </button>
            </div>
          </div>
        </div>

        {/* Create Room: Open (Public) vs Private with Custom Name */}
        <div className="lg:col-span-2 rounded-2xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#1C1C1C] text-[#A3A3A3] flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#FAFAFA]">
                Create Matchmaking Room
              </h2>
              <p className="text-xs text-[#737373] text-[#A3A3A3]">
                Choose between a public Open Room or a protected Private Room.
              </p>
            </div>
          </div>

          {/* Room Name Input */}
          <div className="mb-4">
            <label
              htmlFor="create-room-name-input"
              className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
            >
              Room Name
            </label>
            <input
              id="create-room-name-input"
              type="text"
              maxLength={50}
              placeholder="e.g. Berean Champions Arena"
              value={createRoomName}
              onChange={(e) => setCreateRoomName(e.target.value)}
              className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
            />
          </div>

          {/* Open Room vs Private Room Mode Selector */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <button
              type="button"
              onClick={() => setIsPrivateRoom(false)}
              className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 ${
                !isPrivateRoom
                  ? 'border-[#F97316] bg-[#F97316]/10 text-white ring-1 ring-[#F97316]'
                  : 'border-[#262626] hover:bg-[#1C1C1C] text-[#D4D4D4]'
              }`}
            >
              <Unlock className={`w-5 h-5 shrink-0 mt-0.5 ${!isPrivateRoom ? 'text-[#F97316]' : 'text-[#A3A3A3]'}`} />
              <div>
                <div className="text-xs font-bold flex items-center gap-1.5">
                  <span>Open Room (Public)</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${!isPrivateRoom ? 'bg-[#F97316]/20 text-[#F97316]' : 'bg-[#1C1C1C] text-[#A3A3A3]'}`}>
                    No PIN
                  </span>
                </div>
                <div className="text-[11px] text-[#A3A3A3] mt-0.5">
                  Listed publicly on dashboard. Anyone can join directly without PIN or QR code.
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setIsPrivateRoom(true)}
              className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 ${
                isPrivateRoom
                  ? 'border-[#F97316] bg-[#F97316]/10 text-white ring-1 ring-[#F97316]'
                  : 'border-[#262626] hover:bg-[#1C1C1C] text-[#D4D4D4]'
              }`}
            >
              <Lock className={`w-5 h-5 shrink-0 mt-0.5 ${isPrivateRoom ? 'text-[#F97316]' : 'text-[#A3A3A3]'}`} />
              <div>
                <div className="text-xs font-bold flex items-center gap-1.5">
                  <span>Private Room (Protected)</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${isPrivateRoom ? 'bg-[#F97316]/20 text-[#F97316]' : 'bg-[#1C1C1C] text-[#A3A3A3]'}`}>
                    PIN &amp; QR
                  </span>
                </div>
                <div className="text-[11px] text-[#A3A3A3] mt-0.5">
                  Protected with a unique 6-digit PIN and QR invitation.
                </div>
              </div>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div>
              <label
                htmlFor="mp-cat-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Bible Category
              </label>
              <select
                id="mp-cat-select"
                value={createCategory}
                onChange={(e) => setCreateCategory(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2.5 text-sm text-[#FAFAFA]"
              >
                {BIBLE_CATEGORIES.flatMap((g) => g.items).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="mp-diff-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Difficulty
              </label>
              <select
                id="mp-diff-select"
                value={createDifficulty}
                onChange={(e) => setCreateDifficulty(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2.5 text-sm text-[#FAFAFA]"
              >
                {DIFFICULTY_LEVELS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="mp-count-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Questions
              </label>
              <select
                id="mp-count-select"
                value={createCount}
                onChange={(e) => setCreateCount(Number(e.target.value))}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2.5 text-sm text-[#FAFAFA]"
              >
                <option value={5}>5 Questions</option>
                <option value={8}>8 Questions</option>
                <option value={10}>10 Questions</option>
              </select>
            </div>
          </div>

          <button
            type="button"
            disabled={isConnecting}
            onClick={handleCreateRoom}
            className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 text-white text-sm font-semibold transition-colors shadow-lg shadow-orange-950/30"
          >
            {isConnecting
              ? 'Creating Synchronized Room...'
              : isPrivateRoom
              ? 'Create Private Room (PIN Required)'
              : 'Create Open Room (Public Instant Join)'}
          </button>
        </div>
      </div>

      {/* AVAILABLE ROOMS LIST ON DASHBOARD (Open vs Private Clearly Labeled) */}
      <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-sm">
        <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#262626]">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-[#FAFAFA] flex items-center gap-2">
              <Users className="w-5 h-5 text-[#A3A3A3]" />
              <span>Available Matchmaking Rooms</span>
            </h3>
            <p className="text-xs text-[#737373] text-[#A3A3A3]">
              Open rooms allow instant joining without entering a PIN or QR code.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchAvailableRooms}
            disabled={loadingRooms}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#262626] text-xs font-medium text-[#D4D4D4] hover:bg-[#1C1C1C] transition-colors"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${loadingRooms ? 'animate-spin' : ''}`}
            />
            <span>Refresh</span>
          </button>
        </div>

        {availableRooms.length === 0 ? (
          <div className="py-10 text-center text-[#737373] text-[#A3A3A3] text-xs">
            No active waiting rooms right now. Create an Open Room above or use Quick Match!
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {availableRooms.map((r) => (
              <div
                key={r.roomPin}
                className="p-4 rounded-xl border border-[#262626] bg-[#1C1C1C] flex flex-col justify-between space-y-3"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    {r.isPrivate ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        <Lock className="w-3 h-3" /> Private (PIN)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        <Unlock className="w-3 h-3" /> Open (No PIN)
                      </span>
                    )}

                    <span className="text-[11px] font-mono text-[#737373]">
                      {r.playerCount} player{r.playerCount === 1 ? '' : 's'}
                    </span>
                  </div>

                  <h4 className="font-bold text-sm text-[#FAFAFA] truncate">
                    {r.name}
                  </h4>

                  <div className="text-[11px] text-[#737373] text-[#A3A3A3] mt-1">
                    {r.category} · {r.difficulty}
                  </div>

                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#262626]">
                    <div className="w-5 h-5 rounded-full bg-[#525252] text-white text-[10px] font-bold flex items-center justify-center overflow-hidden">
                      {r.hostAvatar ? (
                        <img
                          src={r.hostAvatar}
                          alt={r.hostName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span>{(r.hostName || 'H').slice(0, 1)}</span>
                      )}
                    </div>
                    <span className="text-[11px] text-[#D4D4D4] truncate">
                      Host: {r.hostName}
                    </span>
                  </div>
                </div>

                {r.isPrivate ? (
                  <button
                    type="button"
                    onClick={() => {
                      setJoinPinInput(r.roomPin);
                      handleJoinByPin(r.roomPin);
                    }}
                    className="w-full py-2 px-3 rounded-lg border border-[#262626] hover:border-[#525252] text-xs font-semibold text-[#FAFAFA] transition-colors"
                  >
                    Enter PIN to Join
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleJoinByPin(r.roomPin)}
                    className="w-full py-2 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
                  >
                    Join Open Room
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
